import { create } from "zustand";
import {
  Node,
  Edge,
  Connection,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  NodeChange,
  EdgeChange,
} from "reactflow";
import {
  Pipeline,
  PipelineBuilderState,
  PipelineStatus,
  ValidationResult,
} from "../types/pipeline";
import {
  isNodeInExecutionFlow,
  validateSubAgentConnection,
} from "../utils/executionFlow";
import { applyValidationToNodes } from "../utils/validationMapper";
import { removeVariableFromPrompt } from "../utils/promptManipulation";
import { useUIStore } from "./index";

interface PipelineBuilderActions {
  // React Flow updates
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  onConnect: (connection: Connection) => void;

  // Node operations
  addNode: (node: Node) => void;
  removeNode: (nodeId: string) => void;
  updateNode: (nodeId: string, updates: Partial<Node>) => void;
  selectNode: (nodeId: string | null) => void;

  // Sub-agent operations (SWRM-121)
  addSubAgent: (
    orchestratorId: string,
    firstNodeId: string,
    subAgentConfig: {
      name: string;
      display_name: string;
      description: string;
    }
  ) => void;
  updateSubAgentName: (
    orchestratorId: string,
    oldName: string,
    newName: string,
    newDisplayName?: string,
    newDescription?: string
  ) => void;
  removeSubAgent: (orchestratorId: string, subAgentName: string) => void;
  assignNodeToSubAgent: (
    nodeId: string,
    orchestratorId: string,
    subAgentName: string
  ) => void;
  detectSubAgentMembership: () => void;

  // Custom input operations
  addCustomInput: (
    nodeId: string,
    inputName: string,
    description?: string
  ) => void;
  removeCustomInput: (nodeId: string, inputName: string) => void;
  setPendingInputConnection: (
    pending: { nodeId: string; connection: Connection } | null
  ) => void;

  // Pipeline metadata
  setMetadata: (metadata: {
    pipelineName?: string;
    pipelineDescription?: string;
    pipelineVersion?: string;
    pipelineStatus?: PipelineStatus;
  }) => void;

  // Pipeline loading/saving
  loadPipeline: (pipeline: Pipeline) => Promise<void>;
  reset: () => void;
  revertChanges: () => void;
  saveOriginalState: () => void;

  // Validation
  setValidationResults: (results: ValidationResult) => void;
  clearValidationResults: () => void;

  // UI state
  setLoading: (isLoading: boolean) => void;
  setSaving: (isSaving: boolean) => void;
  updateEdgeColorsForTheme: (theme: "light" | "dark") => void;
  markDirty: () => void;
  markClean: () => void;
  setPanelHeight: (height: number) => void;
}

const initialState: PipelineBuilderState = {
  pipelineId: null,
  pipelineName: "",
  pipelineDescription: "",
  pipelineVersion: "1.0.0",
  pipelineStatus: "DRAFT",
  nodes: [],
  edges: [],
  loadingWarnings: [],
  selectedNodeId: null,
  validationResults: null,
  validatedAt: null,
  isDirty: false,
  isLoading: false,
  // Responsive default height: max 500px or 35% of viewport height, whichever is smaller
  panelHeight:
    typeof window !== "undefined"
      ? Math.min(700, window.innerHeight * 0.45)
      : 500,
  isSaving: false,
  originalState: null,
  pendingInputConnection: null,
};

export const usePipelineBuilderStore = create<
  PipelineBuilderState & PipelineBuilderActions
>((set, get) => ({
  ...initialState,

  // React Flow updates
  setNodes: (nodes) => set({ nodes, isDirty: true }),

  setEdges: (edges) => set({ edges, isDirty: true }),

  onNodesChange: (changes) => {
    const state = get();

    // Cascade delete sub-agent chains when nodes are removed (SWRM-121)
    const removedNodeIds = changes
      .filter((c: any) => c.type === "remove" && c.id)
      .map((c: any) => c.id);

    const removedSubAgentNodes = state.nodes.filter(
      (node) => removedNodeIds.includes(node.id) && node.data.subAgentName
    );

    // If any sub-agent nodes are being removed, cascade delete downstream chains
    const additionalNodesToRemove = new Set<string>();
    if (removedSubAgentNodes.length > 0) {
      const { findSubAgentChain } = require("../utils/subAgentHelpers");

      for (const removedNode of removedSubAgentNodes) {
        const chainNodes = findSubAgentChain(
          removedNode.id,
          removedNode.data.subAgentName,
          state.nodes,
          state.edges
        );
        chainNodes.forEach((id: string) => additionalNodesToRemove.add(id));
      }
    }

    // Add cascade delete changes for downstream nodes
    const cascadeChanges = Array.from(additionalNodesToRemove)
      .filter((id) => !removedNodeIds.includes(id))
      .map((id) => ({ type: "remove" as const, id }));

    const allChanges = [...changes, ...cascadeChanges];
    const updatedNodes = applyNodeChanges(allChanges, state.nodes);

    // Only mark as dirty for user-initiated changes, not React Flow's internal updates
    // dimensions = React Flow calculating size (don't mark dirty)
    // select = Selection change (don't mark dirty)
    // position/add/remove/reset = User action (mark dirty)
    const hasMeaningfulChanges = changes.some(
      (change) =>
        change.type === "position" ||
        change.type === "add" ||
        change.type === "remove" ||
        change.type === "reset"
    );

    if (hasMeaningfulChanges) {
      // Clear validation errors from nodes when structure changes
      let nodesWithoutValidation = applyValidationToNodes(updatedNodes, null);

      // Clean up orchestrator config for removed sub-agent nodes (SWRM-121)
      if (removedSubAgentNodes.length > 0) {
        nodesWithoutValidation = nodesWithoutValidation.map((node) => {
          if (node.type !== "orchestrator") return node;

          // For each removed sub-agent node, remove the corresponding sub-agent config
          let subAgents = (node.data.config?.sub_agents || []) as any[];

          for (const removedNode of removedSubAgentNodes) {
            if (removedNode.data.orchestratorNodeId === node.id) {
              const subAgentName = removedNode.data.subAgentName;
              subAgents = subAgents.filter((sa) => sa.name !== subAgentName);
            }
          }

          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...node.data.config,
                sub_agents: subAgents,
              },
            },
          };
        });
      }

      // Remove edges connected to deleted nodes
      const allDeletedNodeIds = new Set([
        ...removedNodeIds,
        ...Array.from(additionalNodesToRemove),
      ]);
      const updatedEdges = state.edges.filter(
        (edge) =>
          !allDeletedNodeIds.has(edge.source) &&
          !allDeletedNodeIds.has(edge.target)
      );

      set({
        nodes: nodesWithoutValidation,
        edges: updatedEdges,
        isDirty: true,
      });

      // Auto-revalidate if there were existing validation errors/warnings
      const hasExistingValidation =
        state.validationResults &&
        (state.validationResults.errors.length > 0 ||
          state.validationResults.warnings.length > 0);

      if (hasExistingValidation) {
        // Trigger async revalidation
        setTimeout(async () => {
          try {
            const currentState = get();
            const { buildPipelineSteps } = require("../utils/executionFlow");
            const { apiService } = require("../services/api");
            const steps = buildPipelineSteps(
              currentState.nodes,
              currentState.edges
            );
            const results = await apiService.validatePipeline({ steps });
            get().setValidationResults(results);
          } catch (error) {
            console.error("Auto-revalidation failed:", error);
          }
        }, 500); // Debounce to avoid too many validation calls
      }
    } else {
      set({ nodes: updatedNodes });
    }
  },

  onEdgesChange: (changes) => {
    const state = get();

    // Collect custom input edge removals to handle them inline
    const customInputRemovals: Array<{ nodeId: string; inputName: string }> =
      [];
    console.log(
      "[CustomInput] onEdgesChange called with changes:",
      changes.map((c: any) => ({ type: c.type, id: c.id }))
    );

    changes.forEach((change) => {
      if (change.type === "remove") {
        const edgeId = (change as any).id;
        console.log("[CustomInput] Processing remove change:", edgeId);

        // Try to find edge in current state (may not exist if already removed)
        let edge = state.edges.find((e) => e.id === edgeId);
        console.log("[CustomInput] Found edge in state:", edge);

        // If edge not in state, try to parse from edge ID
        // Edge ID format: data-{source}-{sourceKey}-to-{target}-{inputKey}
        // Split on '-to-' then split the second part on last '-'
        if (!edge && edgeId.startsWith("data-")) {
          console.log("[CustomInput] Edge not in state, parsing ID:", edgeId);
          const parts = edgeId.split("-to-");
          if (parts.length === 2) {
            const targetPart = parts[1]; // e.g., "oneshot_document_1764093400691-task_description"
            // Split on last hyphen to separate target node ID from input key
            const lastHyphenIndex = targetPart.lastIndexOf("-");
            if (lastHyphenIndex > 0) {
              const targetNodeId = targetPart.substring(0, lastHyphenIndex);
              const inputKey = targetPart.substring(lastHyphenIndex + 1);
              console.log("[CustomInput] Parsed:", { targetNodeId, inputKey });

              // Create a pseudo-edge object with the info we need
              edge = {
                id: edgeId,
                source: "",
                target: targetNodeId,
                targetHandle: `input-${inputKey}`,
              } as any;
              console.log("[CustomInput] Created pseudo-edge:", edge);
            }
          }
        }

        if (edge && edge.targetHandle?.startsWith("input-")) {
          const inputName = edge.targetHandle.replace("input-", "");
          const targetNode = state.nodes.find((n) => n.id === edge.target);

          console.log("[CustomInput] Edge removal detected:", {
            edgeId: edge.id,
            targetHandle: edge.targetHandle,
            inputName,
            nodeId: edge.target,
            targetNodeFound: !!targetNode,
            targetNodeId: targetNode?.id,
            processorName: targetNode?.data.processor_name,
            hasCustomInputs: !!targetNode?.data.custom_inputs,
            customInputs: targetNode?.data.custom_inputs,
          });

          // Check if this is a custom input
          const isCustomInput = targetNode?.data.custom_inputs?.some(
            (ci: { name: string; description?: string }) =>
              ci.name === inputName
          );
          console.log("[CustomInput] Is custom input?", isCustomInput);

          if (isCustomInput) {
            console.log("[CustomInput] Adding to removal list:", {
              nodeId: edge.target,
              inputName,
            });
            customInputRemovals.push({ nodeId: edge.target, inputName });
          } else {
            console.log("[CustomInput] NOT a custom input - skipping");
          }
        } else {
          console.log(
            "[CustomInput] Edge does not have input- targetHandle or could not parse"
          );
        }
      }
    });

    console.log(
      "[CustomInput] Total removals to process:",
      customInputRemovals.length
    );

    // Filter out custom input edge removals from changes (we'll handle them inline)
    const filteredChanges = changes.filter((change) => {
      if (change.type === "remove") {
        const edgeId = (change as any).id;

        // Parse target node and input from edge ID if it's a data edge
        if (edgeId.startsWith("data-")) {
          const parts = edgeId.split("-to-");
          if (parts.length === 2) {
            const targetPart = parts[1];
            const lastHyphenIndex = targetPart.lastIndexOf("-");
            if (lastHyphenIndex > 0) {
              const targetNodeId = targetPart.substring(0, lastHyphenIndex);
              const inputKey = targetPart.substring(lastHyphenIndex + 1);
              // Filter out if it's in our custom removals list
              const shouldFilter = customInputRemovals.some(
                (r) => r.nodeId === targetNodeId && r.inputName === inputKey
              );
              console.log("[CustomInput] Filtering edge?", {
                edgeId,
                shouldFilter,
              });
              return !shouldFilter;
            }
          }
        }
      }
      return true;
    });

    console.log(
      "[CustomInput] Filtered changes length:",
      filteredChanges.length
    );

    // If all changes were custom input removals and no other changes, handle separately
    if (filteredChanges.length === 0 && customInputRemovals.length > 0) {
      console.log(
        "[CustomInput] All changes were custom input removals - calling removeCustomInput for each"
      );
      // Just call removeCustomInput for each and return
      customInputRemovals.forEach(({ nodeId, inputName }) => {
        console.log("[CustomInput] Calling removeCustomInput:", {
          nodeId,
          inputName,
        });
        get().removeCustomInput(nodeId, inputName);
      });
      console.log("[CustomInput] Done processing all removals");
      return;
    }

    // If there are no changes at all, return
    if (filteredChanges.length === 0) {
      console.log("[CustomInput] No changes to process - returning");
      return;
    }

    // Capture removed edges BEFORE applying changes (SWRM-121)
    // Note: Edges may already be removed from state.edges, so we parse edge IDs
    const removedEdgeIds = filteredChanges
      .filter((c: any) => c.type === "remove" && c.id)
      .map((c: any) => c.id);

    // Identify orchestrator-to-sub-agent edges (start with 'subagent-')
    const removedSubAgentEdgeIds = removedEdgeIds.filter((id) =>
      id.startsWith("subagent-")
    );

    // Identify execution flow edges within sub-agent chains (start with 'exec-')
    // Edge ID format: exec-{sourceNodeId}-to-{targetNodeId}
    const removedChainEdgeInfo = removedEdgeIds
      .filter((id) => id.startsWith("exec-"))
      .map((id) => {
        // Parse edge ID: exec-{source}-to-{target}
        const match = id.match(/^exec-(.+)-to-(.+)$/);
        if (!match) return null;

        const [, sourceNodeId, targetNodeId] = match;

        // Look up actual nodes to check if they're sub-agent nodes
        // NOTE: Can't parse from ID because fresh nodes might not have dotted format yet
        const sourceNode = state.nodes.find((n) => n.id === sourceNodeId);
        const targetNode = state.nodes.find((n) => n.id === targetNodeId);

        // Both must be sub-agent nodes in the same sub-agent
        if (
          sourceNode?.data?.orchestratorNodeId &&
          targetNode?.data?.orchestratorNodeId &&
          sourceNode.data.subAgentName &&
          targetNode.data.subAgentName &&
          sourceNode.data.orchestratorNodeId ===
            targetNode.data.orchestratorNodeId &&
          sourceNode.data.subAgentName === targetNode.data.subAgentName
        ) {
          return {
            edgeId: id,
            sourceNodeId,
            targetNodeId,
            subAgentName: sourceNode.data.subAgentName,
          };
        }

        return null;
      })
      .filter(Boolean);

    // Now apply the edge changes
    const updatedEdges = applyEdgeChanges(filteredChanges, state.edges);

    // Only mark as dirty for user-initiated changes, not selection
    // select = Selection change (don't mark dirty)
    // add/remove/reset = User action (mark dirty)
    const hasMeaningfulChanges =
      filteredChanges.some(
        (change) =>
          change.type === "add" ||
          change.type === "remove" ||
          change.type === "reset"
      ) || customInputRemovals.length > 0;

    if (hasMeaningfulChanges) {
      // Clear validation errors from nodes when edges change
      let nodesWithoutValidation = applyValidationToNodes(state.nodes, null);

      // Handle custom input removals inline
      if (customInputRemovals.length > 0) {
        console.log("[CustomInput] Processing removals inline...");
        nodesWithoutValidation = nodesWithoutValidation.map((node) => {
          const removal = customInputRemovals.find((r) => r.nodeId === node.id);
          if (!removal) return node;

          console.log("[CustomInput] Processing node:", {
            nodeId: node.id,
            removing: removal.inputName,
            before: node.data.custom_inputs,
          });

          // Remove this custom input
          const customInputs = node.data.custom_inputs || [];
          const newCustomInputs = customInputs.filter(
            (ci: { name: string; description?: string }) =>
              ci.name !== removal.inputName
          );

          // Remove from input_artifact_keys (it's an object, not an array)
          const inputArtifactKeys = node.data.input_artifact_keys || {};
          const newInputArtifactKeys = { ...inputArtifactKeys };
          delete newInputArtifactKeys[removal.inputName];

          console.log("[CustomInput] After removal:", {
            custom_inputs: newCustomInputs,
            input_artifact_keys: newInputArtifactKeys,
          });

          // Remove variable from prompt
          const processorName = node.data.processor_name;
          const userPromptKey = `${processorName}_user`;
          const customPrompts = node.data.config?.custom_prompts || {};
          const currentUserPrompt = customPrompts[userPromptKey];

          let updatedCustomPrompts = customPrompts;
          if (currentUserPrompt) {
            const updatedPrompt = removeVariableFromPrompt(
              currentUserPrompt,
              removal.inputName
            );
            updatedCustomPrompts = {
              ...customPrompts,
              [userPromptKey]: updatedPrompt,
            };
          }

          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              custom_inputs: newCustomInputs,
              input_artifact_keys: newInputArtifactKeys,
              validation_errors: undefined, // Clear validation errors
              validation_warnings: undefined, // Clear validation warnings
              config: {
                ...node.data.config,
                custom_inputs: newCustomInputs, // Also update in config
                custom_prompts: updatedCustomPrompts,
              },
            },
          };

          console.log("[CustomInput] Updated node data:", {
            nodeId: updatedNode.id,
            custom_inputs: updatedNode.data.custom_inputs,
          });

          return updatedNode;
        });
        console.log("[CustomInput] Finished processing removals");
      }

      // Cascade delete sub-agent chains (SWRM-121)
      const nodesToDelete = new Set<string>();
      let finalEdges = updatedEdges;
      const { findSubAgentChain } = require("../utils/subAgentHelpers");

      // Case 1: Orchestrator-to-sub-agent edge removed - delete entire sub-agent
      if (removedSubAgentEdgeIds.length > 0) {
        // Find all nodes to delete by identifying sub-agent chains
        for (const edgeId of removedSubAgentEdgeIds) {
          // Edge ID format: subagent-{orchestratorId}-to-{targetNodeId}
          const match = edgeId.match(/^subagent-([^-]+)-to-(.+)$/);
          if (!match) continue;

          const [, orchestratorId, targetNodeId] = match;

          // Look up the target node to get its sub-agent name
          // NOTE: Can't parse from ID because fresh nodes might not have dotted format yet
          const targetNode = nodesWithoutValidation.find(
            (n) => n.id === targetNodeId
          );
          if (!targetNode?.data?.subAgentName) continue;

          const subAgentName = targetNode.data.subAgentName;

          // Find all nodes belonging to this sub-agent and mark for deletion
          const subAgentNodes = nodesWithoutValidation.filter(
            (n) =>
              n.data.orchestratorNodeId === orchestratorId &&
              n.data.subAgentName === subAgentName
          );

          subAgentNodes.forEach((n) => nodesToDelete.add(n.id));
        }
      }

      // Case 2: Execution flow edge within sub-agent chain removed - delete downstream nodes
      if (removedChainEdgeInfo.length > 0) {
        for (const edgeInfo of removedChainEdgeInfo) {
          if (!edgeInfo) continue;

          // Find all downstream nodes starting from the target
          const downstreamNodes = findSubAgentChain(
            edgeInfo.targetNodeId,
            edgeInfo.subAgentName,
            nodesWithoutValidation,
            state.edges
          );
          downstreamNodes.forEach((id: string) => nodesToDelete.add(id));
        }
      }

      if (nodesToDelete.size > 0) {
        // Delete all marked nodes
        nodesWithoutValidation = nodesWithoutValidation.filter(
          (n) => !nodesToDelete.has(n.id)
        );

        // Remove all edges connected to cascade-deleted nodes
        finalEdges = updatedEdges.filter(
          (e) => !nodesToDelete.has(e.source) && !nodesToDelete.has(e.target)
        );

        // Clean up orchestrator configs (remove sub-agent entries)
        nodesWithoutValidation = nodesWithoutValidation.map((node) => {
          if (node.type !== "orchestrator") return node;

          let subAgents = (node.data.config?.sub_agents || []) as any[];

          // Case 1: Remove sub-agents where the orchestrator → sub-agent edge was explicitly deleted
          // Only applies when removedSubAgentEdgeIds has entries (user deleted the green diamond edge)
          if (removedSubAgentEdgeIds.length > 0) {
            for (const edgeId of removedSubAgentEdgeIds) {
              const match = edgeId.match(/^subagent-([^-]+)-to-(.+)$/);
              if (!match || match[1] !== node.id) continue;

              const [, , targetNodeId] = match;
              const targetNode = state.nodes.find((n) => n.id === targetNodeId);
              if (targetNode?.data?.subAgentName) {
                subAgents = subAgents.filter(
                  (sa) => sa.name !== targetNode.data.subAgentName
                );
              }
            }
          }

          // Case 2: Remove sub-agents where ALL nodes have been deleted (cascade from internal edge removal)
          // This ensures sub-agents are only removed from config if the entire chain is gone
          subAgents = subAgents.filter((sa) => {
            // Check if any nodes still exist for this sub-agent
            const hasRemainingNodes = nodesWithoutValidation.some(
              (n) =>
                n.data.orchestratorNodeId === node.id &&
                n.data.subAgentName === sa.name
            );
            return hasRemainingNodes;
          });

          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...node.data.config,
                sub_agents: subAgents,
              },
            },
          };
        });
      }

      // Detect and update sub-agent membership based on new edges (SWRM-121)
      const {
        detectAndUpdateSubAgentMembership,
      } = require("../utils/subAgentHelpers");
      const nodesWithUpdatedMembership = detectAndUpdateSubAgentMembership(
        nodesWithoutValidation,
        finalEdges
      );

      if (customInputRemovals.length > 0) {
        console.log("[CustomInput] Final state before set():", {
          nodesWithCustomInputs: nodesWithUpdatedMembership
            .filter((n: any) => n.data.custom_inputs?.length > 0)
            .map((n: any) => ({
              id: n.id,
              custom_inputs: n.data.custom_inputs,
            })),
        });
      }

      set({
        edges: finalEdges,
        nodes: nodesWithUpdatedMembership,
        isDirty: true,
      });

      // Auto-revalidate if there were existing validation errors/warnings
      const hasExistingValidation =
        state.validationResults &&
        (state.validationResults.errors.length > 0 ||
          state.validationResults.warnings.length > 0);

      if (hasExistingValidation) {
        // Trigger async revalidation
        setTimeout(async () => {
          try {
            const currentState = get();
            const { buildPipelineSteps } = require("../utils/executionFlow");
            const { apiService } = require("../services/api");
            const steps = buildPipelineSteps(
              currentState.nodes,
              currentState.edges
            );
            const results = await apiService.validatePipeline({ steps });
            get().setValidationResults(results);
          } catch (error) {
            console.error("Auto-revalidation failed:", error);
          }
        }, 500); // Debounce to avoid too many validation calls
      }
    } else {
      set({ edges: updatedEdges });
    }
  },

  onConnect: (connection) => {
    console.log('[onConnect] Called with connection:', connection);
    const state = get();
    const { edges } = state;

    // Check if connecting to "add-input" handle (dynamic input creation)
    if (connection.targetHandle === "add-input" && connection.target) {
      // Store pending connection and trigger dialog (handled by page component)
      set({
        pendingInputConnection: { nodeId: connection.target, connection },
      });
      return; // Don't create edge yet - wait for user to name the input
    }

    // Determine if this is an execution flow connection
    const isExecutionFlow =
      connection.sourceHandle === "next_node" ||
      connection.sourceHandle === "sub_agent_output" || // SWRM-121: Orchestrator to sub-agent
      connection.targetHandle === "previous_node";

    if (isExecutionFlow) {
      // Validation for execution flow connections

      // Rule 1: Each node can only have ONE outgoing execution edge
      const existingOutgoingExec = edges.find(
        (e) => e.source === connection.source && e.sourceHandle === "next_node"
      );
      if (existingOutgoingExec) {
        console.warn("Node already has an outgoing execution edge");
        return; // Silently fail - user will see no connection made
      }

      // Rule 2: Each node can only have ONE incoming execution edge
      const existingIncomingExec = edges.find(
        (e) =>
          e.target === connection.target && e.targetHandle === "previous_node"
      );
      if (existingIncomingExec) {
        console.warn("Node already has an incoming execution edge");
        return; // Silently fail
      }

      // Rule 3: Cannot connect to self
      if (connection.source === connection.target) {
        console.warn("Cannot connect node to itself");
        return;
      }

      // Rule 4: Check for cycles (simple check - more comprehensive check in validation)
      const wouldCreateCycle = (
        sourceId: string,
        targetId: string
      ): boolean => {
        const visited = new Set<string>();
        const queue = [targetId];

        while (queue.length > 0) {
          const current = queue.shift()!;
          if (visited.has(current)) continue;
          if (current === sourceId) return true; // Cycle detected

          visited.add(current);

          const nextEdges = edges.filter(
            (e) => e.source === current && e.sourceHandle === "next_node"
          );
          nextEdges.forEach((e) => {
            if (e.target) queue.push(e.target);
          });
        }

        return false;
      };

      if (
        connection.source &&
        connection.target &&
        wouldCreateCycle(connection.source, connection.target)
      ) {
        console.warn("Would create execution cycle");
        return;
      }

      // Add execution flow edge with custom style
      // Use green for sub-agent connections (SWRM-121)
      const isSubAgentConnection =
        connection.sourceHandle === "sub_agent_output";

      // CRITICAL: Set explicit edge IDs so deletion logic can find them
      // Format must match what loadPipeline uses:
      // - subagent-{orchestratorId}-to-{targetNodeId} for orchestrator → first sub-agent node
      // - exec-{source}-to-{target} for all other execution flow edges
      const edgeId = isSubAgentConnection
        ? `subagent-${connection.source}-to-${connection.target}`
        : `exec-${connection.source}-to-${connection.target}`;

      // Get theme-aware execution flow color
      const theme = useUIStore.getState().theme;
      const executionFlowColor = theme === "dark" ? "#e5e5e5" : "#64748b";

      const newEdge = {
        ...connection,
        id: edgeId,
        type: isSubAgentConnection ? ("step" as const) : ("execution" as const), // L-shaped for sub-agents
        animated: false,
        style: {
          stroke: isSubAgentConnection ? "#22c55e" : executionFlowColor, // Green for sub-agents, theme-aware for normal
          strokeWidth: isSubAgentConnection ? 3 : 4,
        },
      };

      const updatedEdges = addEdge(newEdge, edges);

      // Detect and update sub-agent membership (SWRM-121)
      // NOTE: onEdgesChange is NOT triggered by onConnect, so we must do this here
      const {
        detectAndUpdateSubAgentMembership,
      } = require("../utils/subAgentHelpers");
      const nodesWithUpdatedMembership = detectAndUpdateSubAgentMembership(
        state.nodes,
        updatedEdges
      );

      set({
        edges: updatedEdges,
        nodes: nodesWithUpdatedMembership,
        isDirty: true,
      });

      // Auto-revalidate if there were existing validation errors/warnings
      const hasExistingValidation =
        state.validationResults &&
        (state.validationResults.errors.length > 0 ||
          state.validationResults.warnings.length > 0);

      if (hasExistingValidation) {
        setTimeout(async () => {
          try {
            const currentState = get();
            const { buildPipelineSteps } = require("../utils/executionFlow");
            const { apiService } = require("../services/api");
            const steps = buildPipelineSteps(
              currentState.nodes,
              currentState.edges
            );
            const results = await apiService.validatePipeline({ steps });
            get().setValidationResults(results);
          } catch (error) {
            console.error("Auto-revalidation failed:", error);
          }
        }, 500);
      }
    } else {
      // Validation for data flow connections

      // Rule 1: Target node must be in execution flow (per Q1)
      if (
        connection.target &&
        !isNodeInExecutionFlow(connection.target, edges, state.nodes)
      ) {
        console.warn("Connect target node to execution flow first");
        return; // Silently fail - user will see disabled handles
      }

      // Rule 2: Source node must be in execution flow
      if (
        connection.source &&
        !isNodeInExecutionFlow(connection.source, edges, state.nodes)
      ) {
        console.warn("Connect source node to execution flow first");
        return;
      }

      // Rule 3: Validate sub-agent connections (SWRM-121)
      const subAgentValidation = validateSubAgentConnection(
        connection.source || "",
        connection.sourceHandle || "",
        connection.target || "",
        connection.targetHandle || "",
        state.nodes
      );
      if (!subAgentValidation.valid) {
        console.warn(
          `[Sub-Agent Connection Blocked] ${subAgentValidation.reason}`
        );
        return;
      }

      // Rule 4: Cannot connect same handle to multiple sources
      const existingDataConnection = edges.find(
        (e) =>
          e.target === connection.target &&
          e.targetHandle === connection.targetHandle &&
          e.sourceHandle !== "next_node"
      );
      if (existingDataConnection) {
        console.warn("Input already connected - existing edge:", existingDataConnection);
        console.warn("Attempted connection:", connection);
        return;
      }

      // Check if this is an LLM output connection (for styling)
      const isLLMOutput = connection.sourceHandle === "llm_output";
      
      // Get source output type for type-based coloring
      const sourceNode = state.nodes.find(n => n.id === connection.source);
      const sourceOutputKey = connection.sourceHandle?.replace('output-', '');
      const sourceOutputType = sourceNode?.data.output_types?.[sourceOutputKey || ''] || 'string';
      
      // Debug logging for type detection
      console.log('[onConnect] Type detection:', {
        sourceNodeId: connection.source,
        sourceHandle: connection.sourceHandle,
        sourceOutputKey,
        output_types: sourceNode?.data.output_types,
        detectedType: sourceOutputType,
        targetHandle: connection.targetHandle,
      });
      
      // Get type-based color
      const { getArtifactTypeColor } = require('../utils/artifactTypes');
      const typeColor = getArtifactTypeColor(sourceOutputType);
      
      // Validate single vector_db input rule
      if (sourceOutputType === 'vector_db' && connection.target) {
        const targetNode = state.nodes.find(n => n.id === connection.target);
        const existingVectorInput = targetNode?.data.input_artifact_keys ?
          Object.values(targetNode.data.input_artifact_keys).find(
            (input: any) => input.type === 'vector_db'
          ) : null;
        
        if (existingVectorInput) {
          console.warn('Processor can only have one vector_db input');
          return;
        }
      }

      // Add data flow edge with type-based styling
      const newEdge = {
        ...connection,
        type: "default" as const,
        animated: false,
        style: isLLMOutput
          ? {
              stroke: "#9333ea", // purple-600 for LLM outputs
              strokeWidth: 2,
              strokeDasharray: "5,5", // dashed line
            }
          : {
              stroke: typeColor, // Type-based color!
              strokeWidth: 2,
            },
        data: {
          outputType: sourceOutputType, // Store type on edge for reference
        },
      };

      const updatedEdges = addEdge(newEdge, edges);
      
      // Update target node's input_artifact_keys to include type
      const targetInputName = connection.targetHandle?.replace('input-', '');
      const targetNode = state.nodes.find(n => n.id === connection.target);
      
      if (targetNode && targetInputName && sourceOutputKey) {
        const updatedNodes = state.nodes.map(node => {
          if (node.id === connection.target) {
            const newInputArtifactKeys = {
              ...node.data.input_artifact_keys,
              [targetInputName]: {
                source: 'artifact' as const,
                from_step: connection.source || '',
                key: sourceOutputKey,
                type: sourceOutputType, // STORE TYPE!
              }
            };
            
            return {
              ...node,
              data: {
                ...node.data,
                input_artifact_keys: newInputArtifactKeys,
              }
            };
          }
          return node;
        });
        
        set({ 
          edges: updatedEdges,
          nodes: updatedNodes,
          isDirty: true 
        });
      } else {
        set({ edges: updatedEdges, isDirty: true });
      }

      // Auto-revalidate if there were existing validation errors/warnings
      const hasExistingValidation =
        state.validationResults &&
        (state.validationResults.errors.length > 0 ||
          state.validationResults.warnings.length > 0);

      if (hasExistingValidation) {
        setTimeout(async () => {
          try {
            const currentState = get();
            const { buildPipelineSteps } = require("../utils/executionFlow");
            const { apiService } = require("../services/api");
            const steps = buildPipelineSteps(
              currentState.nodes,
              currentState.edges
            );
            const results = await apiService.validatePipeline({ steps });
            get().setValidationResults(results);
          } catch (error) {
            console.error("Auto-revalidation failed:", error);
          }
        }, 500);
      }
    }
  },

  // Node operations
  addNode: (node) => {
    const state = get();
    // Clear validation errors from all nodes when adding a new node
    const nodesWithoutValidation = applyValidationToNodes(
      [...state.nodes, node],
      null
    );
    set({ nodes: nodesWithoutValidation, isDirty: true });
  },

  removeNode: (nodeId) => {
    const state = get();
    // Don't allow removing system input node
    if (nodeId === "input") return;

    // Check if this node belongs to a sub-agent (SWRM-121)
    const nodeToRemove = state.nodes.find((n) => n.id === nodeId);
    const isSubAgentNode = nodeToRemove?.data.subAgentName;
    const orchestratorNodeId = nodeToRemove?.data.orchestratorNodeId;

    // Cascade delete: Find all downstream nodes in the same sub-agent chain (SWRM-121)
    const nodesToDelete = new Set<string>([nodeId]);

    if (isSubAgentNode && orchestratorNodeId) {
      // Find all nodes downstream in the same sub-agent chain
      const { findSubAgentChain } = require("../utils/subAgentHelpers");
      const chainNodes = findSubAgentChain(
        nodeId,
        isSubAgentNode,
        state.nodes,
        state.edges
      );
      chainNodes.forEach((id: string) => nodesToDelete.add(id));
    }

    // Remove all nodes in the chain
    const updatedNodes = state.nodes.filter(
      (node) => !nodesToDelete.has(node.id)
    );

    // Remove all edges connected to deleted nodes
    const updatedEdges = state.edges.filter(
      (edge) =>
        !nodesToDelete.has(edge.source) && !nodesToDelete.has(edge.target)
    );

    // Clear validation errors from remaining nodes
    let nodesWithoutValidation = applyValidationToNodes(updatedNodes, null);

    // Clean up orchestrator config if this was a sub-agent node (SWRM-121)
    if (isSubAgentNode && orchestratorNodeId) {
      nodesWithoutValidation = nodesWithoutValidation.map((node) => {
        if (node.id !== orchestratorNodeId || node.type !== "orchestrator")
          return node;

        // Remove the sub-agent from config
        const subAgents = (node.data.config?.sub_agents || []) as any[];
        const updatedSubAgents = subAgents.filter(
          (sa) => sa.name !== isSubAgentNode
        );

        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...node.data.config,
              sub_agents: updatedSubAgents,
            },
          },
        };
      });
    }

    set({ nodes: nodesWithoutValidation, edges: updatedEdges, isDirty: true });
  },

  updateNode: (nodeId, updates) => {
    const state = get();
    const updatedNodes = state.nodes.map((node) =>
      node.id === nodeId ? { ...node, ...updates } : node
    );
    set({ nodes: updatedNodes, isDirty: true });
  },

  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),

  // Pipeline metadata
  setMetadata: (metadata) => {
    set({ ...metadata, isDirty: true });
  },

  // Pipeline loading/saving
  loadPipeline: async (pipeline) => {
    // Fetch processor registry to get accurate metadata
    const { apiService } = require("../services/api");
    let processorRegistry: any[] = [];
    try {
      processorRegistry = await apiService.getProcessors();
    } catch (error) {
      console.error("Failed to fetch processor registry:", error);
    }

    // Create a map for quick lookups (using processor_name from API)
    const processorMap = new Map(
      processorRegistry.map((p) => [p.processor_name, p])
    );

    // Convert pipeline steps to React Flow nodes
    const processorNodes: Node[] = [];
    const subAgentNodes: Node[] = [];
    const subAgentEdges: Edge[] = [];

    // Arrays to collect nodes, edges, and data edges for recursive processing
    const dataEdgesFromSubAgents: Edge[] = [];

    // Helper function to recursively load sub-agents for any orchestrator (including nested ones)
    const loadSubAgentsRecursive = (
      orchestratorStepId: string,
      orchestratorPosition: { x: number; y: number },
      subAgents: any[],
      depth: number = 0
    ) => {
      subAgents.forEach((subAgent, subAgentIndex) => {
        if (!subAgent.steps || subAgent.steps.length === 0) return;

        // Sub-agent layout: each chain starts below and to the right of orchestrator
        // Multiple chains stack vertically below each other
        const NODE_WIDTH = 280;
        const GAP = 55;
        const HORIZONTAL_SPACING = NODE_WIDTH + GAP; // 335px (same as main pipeline)
        const VERTICAL_SPACING = 300; // Spacing between sub-agent chains
        
        // Adjust vertical spacing based on nesting depth to prevent overlap
        const depthOffset = depth * 200;

        const baseX = orchestratorPosition.x + 350; // To the right of orchestrator
        const baseY =
          orchestratorPosition.y + 450 + subAgentIndex * VERTICAL_SPACING + depthOffset;

        console.log(
          `🔧 SUB-AGENT LAYOUT (depth ${depth}): Chain ${subAgentIndex} for orchestrator ${orchestratorStepId} at (${orchestratorPosition.x}, ${orchestratorPosition.y})`
        );
        console.log(
          `🔧 SUB-AGENT LAYOUT (depth ${depth}): Chain starts at (${baseX}, ${baseY})`
        );

        subAgent.steps.forEach((subStep: any, stepIndex: number) => {
          // Remove .# placeholder from step_id for display
          const displayStepId = subStep.step_id.replace(".#", "");

          // Get processor metadata
          const subProcessorMeta = processorMap.get(subStep.processor_name);

          // Use saved position if available, otherwise calculate default position
          const position = subStep.ui_position || {
            x: baseX + stepIndex * HORIZONTAL_SPACING,
            y: baseY,
          };

          console.log(
            `🔧 SUB-AGENT LAYOUT (depth ${depth}): Step ${stepIndex} (${displayStepId}) at (${position.x}, ${position.y})`
          );

          // Extract custom_inputs from config if present (for dynamic input processors)
          const subCustomInputs = subStep.config?.custom_inputs || undefined;

          // Extract dynamic_outputs from config if present (from <<variable>> syntax in prompts)
          // If not in config (old pipelines), parse from prompt
          let subDynamicOutputs = subStep.config?.dynamic_outputs || undefined;
          
          // For agentic processors, prompts can be in different locations:
          // - config.user_prompt (oneshot processors)
          // - config.prompts.user_prompt (most agentic processors)
          // - config.prompts.vector_user (vector processor specifically)
          const subUserPrompt = 
            subStep.config?.user_prompt || 
            subStep.config?.prompts?.user_prompt ||
            subStep.config?.prompts?.vector_user;
          
          console.log(`[DYNAMIC-OUTPUTS] Loading sub-agent step ${displayStepId}:`, {
            hasConfigOutputs: !!subStep.config?.dynamic_outputs,
            configOutputs: subStep.config?.dynamic_outputs,
            hasUserPrompt: !!subUserPrompt,
            userPromptLocation: subStep.config?.user_prompt 
              ? 'config.user_prompt' 
              : subStep.config?.prompts?.user_prompt 
              ? 'config.prompts.user_prompt' 
              : subStep.config?.prompts?.vector_user
              ? 'config.prompts.vector_user'
              : 'none',
          });
          
          if (!subDynamicOutputs && subUserPrompt) {
            const { parseOutputDeclarations } = require('../utils/dynamicOutputs');
            subDynamicOutputs = parseOutputDeclarations(subUserPrompt);
            console.log(`[DYNAMIC-OUTPUTS] Parsed from prompt for sub-agent ${displayStepId}:`, subDynamicOutputs);
          }
          
          console.log(`[DYNAMIC-OUTPUTS] Final dynamic_outputs for sub-agent ${displayStepId}:`, subDynamicOutputs);

          // SWRM-133: For A2A processors, extract input keys from input_schema in config
          let subRequiredInputKeys =
            subProcessorMeta?.required_input_keys || [];
          let subOptionalInputKeys =
            subProcessorMeta?.optional_input_keys || [];

          if (
            subStep.processor_name === "a2a_agent" &&
            subStep.config?.input_schema
          ) {
            const inputSchema = subStep.config.input_schema;
            subRequiredInputKeys = [];
            subOptionalInputKeys = [];

            if (inputSchema.properties) {
              // Get schema-level required array (correct JSON Schema format)
              const requiredFields = inputSchema.required || [];
              Object.entries(inputSchema.properties).forEach(
                ([key, prop]: [string, any]) => {
                  if (requiredFields.includes(key)) {
                    subRequiredInputKeys.push(key);
                  } else {
                    subOptionalInputKeys.push(key);
                  }
                }
              );
            }
          }

          // Determine if this sub-agent step is itself an orchestrator
          const isNestedOrchestrator = subProcessorMeta?.processor_type?.toUpperCase() === "ORCHESTRATOR";

          const subNode = {
            id: displayStepId,
            type: isNestedOrchestrator ? "orchestrator" : "processor",
            position,
            data: {
              step_id: displayStepId,
              name: subStep.name,
              processor_name: subStep.processor_name,
              processor_type: (
                subProcessorMeta?.processor_type || "SCRIPTED"
              ).toUpperCase() as any,
              required_input_keys: subRequiredInputKeys,
              optional_input_keys: subOptionalInputKeys,
              output_keys: subProcessorMeta?.output_keys || [],
              output_types: subProcessorMeta?.output_types || {},
              config: subStep.config || {},
              custom_inputs: subCustomInputs,
              dynamic_outputs: subDynamicOutputs,
              input_artifact_keys: subStep.input_artifact_keys || {}, // Track connected inputs with full metadata including type
              depends_on: [],
              isLocked: false,
              orchestratorNodeId: orchestratorStepId,
              subAgentName: subAgent.name,
              prompt_anchor: subStep.prompt_anchor, // Stable prompt identifier
            },
            draggable: true,
            deletable: true,
            selectable: true,
          };

          subAgentNodes.push(subNode);

          // Create edge from parent orchestrator to first sub-agent node
          if (stepIndex === 0) {
            subAgentEdges.push({
              id: `subagent-${orchestratorStepId}-to-${displayStepId}`,
              source: orchestratorStepId,
              sourceHandle: "sub_agent_output",
              target: displayStepId,
              targetHandle: "previous_node",
              type: "step",
              animated: false,
              style: {
                stroke: "#22c55e",
                strokeWidth: 3,
              },
            });
          }

          // Create execution edge to next step in chain
          if (stepIndex < subAgent.steps.length - 1) {
            const nextStepId = subAgent.steps[stepIndex + 1].step_id.replace(
              ".#",
              ""
            );
            const theme = useUIStore.getState().theme;
            const executionFlowColor =
              theme === "dark" ? "#e5e5e5" : "#64748b";

            subAgentEdges.push({
              id: `exec-${displayStepId}-to-${nextStepId}`,
              source: displayStepId,
              sourceHandle: "next_node",
              target: nextStepId,
              targetHandle: "previous_node",
              type: "execution",
              animated: false,
              style: {
                stroke: executionFlowColor,
                strokeWidth: 4,
              },
            });
          }

          // Create data edges for this sub-agent step
          Object.entries(subStep.input_artifact_keys || {}).forEach(
            ([inputKey, ref]: [string, any]) => {
              let sourceStepId = ref.from_step.includes(".#")
                ? ref.from_step.replace(".#", "")
                : ref.from_step;

              // Fix: If source doesn't have orchestrator prefix, check if it's a sub-agent node
              // Backend stores short IDs for sub-agent connections, but frontend needs full IDs
              if (
                !sourceStepId.includes(".") &&
                sourceStepId !== orchestratorStepId
              ) {
                // Check if this source is in the same sub-agent chain
                const sourceInSameChain = subAgent.steps.find(
                  (s: any) =>
                    s.step_id.replace(".#", "") === sourceStepId ||
                    s.step_id.includes(sourceStepId)
                );
                if (sourceInSameChain) {
                  // It's a sub-agent node - needs full prefix
                  sourceStepId = sourceInSameChain.step_id.replace(".#", "");
                }
              }

              // Check if this is an LLM output connection (SWRM-121)
              const isLLMOutput = ref.source === "orchestrator_llm";

              dataEdgesFromSubAgents.push({
                id: `data-${sourceStepId}-${ref.key}-to-${displayStepId}-${inputKey}`,
                source: sourceStepId,
                sourceHandle: isLLMOutput
                  ? "llm_output"
                  : `output-${ref.key}`,
                target: displayStepId,
                targetHandle: `input-${inputKey}`,
                type: "default",
                animated: false,
                style: isLLMOutput
                  ? {
                      stroke: "#9333ea", // purple
                      strokeWidth: 2,
                      strokeDasharray: "5,5", // dashed
                    }
                  : {
                      stroke: "#3b82f6", // blue
                      strokeWidth: 2,
                    },
              });
            }
          );

          // RECURSIVE CALL: If this sub-agent step is itself an orchestrator, load its sub-agents
          if (isNestedOrchestrator && subStep.config?.sub_agents) {
            console.log(
              `🔧 RECURSIVE SUB-AGENT LOAD: Processing nested orchestrator ${displayStepId} at depth ${depth + 1}`
            );
            loadSubAgentsRecursive(
              displayStepId,
              position,
              subStep.config.sub_agents,
              depth + 1
            );
          }
        });
      });
    };

    // First pass: Create main pipeline nodes (including orchestrators)
    pipeline.steps.forEach((step, index) => {
      // Get processor metadata from registry
      const processorMeta = processorMap.get(step.processor_name);

      // Determine processor type
      const processorType = (
        processorMeta?.processor_type ||
        (step.processor_name.includes("coding")
          ? "AGENTIC"
          : step.processor_name.includes("jira") ||
            step.processor_name.includes("github") ||
            step.processor_name === "system"
          ? "UTILITY"
          : "SCRIPTED")
      ).toUpperCase();

      // Determine node type (orchestrator vs regular processor vs input processor)
      let nodeType: string;
      let isDeletable = true;
      let isDraggable = true;

      if (step.processor_name === "input") {
        // SWRM-128: Input processor is a special node type
        nodeType = "inputProcessor";
        isDeletable = false; // Cannot be deleted
        isDraggable = true; // Can be repositioned for visual layout
      } else if (processorType === "ORCHESTRATOR") {
        nodeType = "orchestrator";
      } else {
        nodeType = "processor";
      }

      // Extract custom_inputs from config if present (for dynamic input processors)
      const customInputs = step.config?.custom_inputs || undefined;

      // Extract dynamic_outputs from config if present (from <<variable>> syntax in prompts)
      // If not in config (old pipelines), parse from prompt
      let dynamicOutputs = step.config?.dynamic_outputs || undefined;
      
      // For agentic processors, prompts can be in different locations:
      // - config.user_prompt (oneshot processors)
      // - config.prompts.user_prompt (most agentic processors)
      // - config.prompts.vector_user (vector processor specifically)
      const userPrompt = 
        step.config?.user_prompt || 
        step.config?.prompts?.user_prompt ||
        step.config?.prompts?.vector_user;
      
      console.log(`[DYNAMIC-OUTPUTS] Loading step ${step.step_id}:`, {
        hasConfigOutputs: !!step.config?.dynamic_outputs,
        configOutputs: step.config?.dynamic_outputs,
        hasUserPrompt: !!userPrompt,
        userPromptLocation: step.config?.user_prompt 
          ? 'config.user_prompt' 
          : step.config?.prompts?.user_prompt 
          ? 'config.prompts.user_prompt' 
          : step.config?.prompts?.vector_user
          ? 'config.prompts.vector_user'
          : 'none',
      });
      
      if (!dynamicOutputs && userPrompt) {
        const { parseOutputDeclarations } = require('../utils/dynamicOutputs');
        dynamicOutputs = parseOutputDeclarations(userPrompt);
        console.log(`[DYNAMIC-OUTPUTS] Parsed from prompt for ${step.step_id}:`, dynamicOutputs);
      }
      
      console.log(`[DYNAMIC-OUTPUTS] Final dynamic_outputs for ${step.step_id}:`, dynamicOutputs);

      // SWRM-133: For A2A processors, extract input keys from input_schema in config
      let requiredInputKeys = processorMeta?.required_input_keys || [];
      let optionalInputKeys = processorMeta?.optional_input_keys || [];

      if (step.processor_name === "a2a_agent" && step.config?.input_schema) {
        const inputSchema = step.config.input_schema;
        requiredInputKeys = [];
        optionalInputKeys = [];

        if (inputSchema.properties) {
          // Get schema-level required array (correct JSON Schema format)
          const requiredFields = inputSchema.required || [];
          Object.entries(inputSchema.properties).forEach(
            ([key, prop]: [string, any]) => {
              if (requiredFields.includes(key)) {
                requiredInputKeys.push(key);
              } else {
                optionalInputKeys.push(key);
              }
            }
          );
        }
      }

      const mainNode = {
        id: step.step_id,
        type: nodeType,
        position: step.ui_position || {
          x: 250 + (index % 3) * 300,
          y: 100 + Math.floor(index / 3) * 200,
        },
        data: {
          step_id: step.step_id,
          name: step.name,
          processor_name: step.processor_name,
          processor_type: processorType as any,
          required_input_keys: requiredInputKeys,
          optional_input_keys: optionalInputKeys,
          output_keys: processorMeta?.output_keys || [],
          output_types: processorMeta?.output_types || {},
          config: step.config,
          custom_inputs: customInputs, // Extract custom inputs for prompt editor
          dynamic_outputs: dynamicOutputs, // Extract dynamic outputs for UI rendering
          input_artifact_keys: step.input_artifact_keys || {}, // Track connected inputs with full metadata including type
          depends_on: Object.values(step.input_artifact_keys || {}).map(
            (ref: any) => ref.from_step
          ),
          prompt_anchor: step.prompt_anchor, // Stable prompt identifier
          isLocked: false,
          order: step.order,
        },
        draggable: isDraggable,
        deletable: isDeletable,
        selectable: true,
      };

      processorNodes.push(mainNode);

      // Second pass: If this is an orchestrator, load its sub-agents recursively
      if (nodeType === "orchestrator" && step.config?.sub_agents) {
        loadSubAgentsRecursive(
          step.step_id,
          mainNode.position,
          step.config.sub_agents,
          0 // depth starts at 0 for top-level orchestrators
        );
      }
    });

    // SWRM-128: Input processor is now saved in backend as part of steps
    // Only add SYSTEM_INPUT_NODE if it's not already in processorNodes
    const hasInputNode = processorNodes.some((n) => n.id === "input");
    const nodes: Node[] = hasInputNode
      ? [...processorNodes, ...subAgentNodes]
      : (() => {
          const { SYSTEM_INPUT_NODE } = require("../constants/systemNodes");
          const inputNodeWithOrder = {
            ...SYSTEM_INPUT_NODE,
            data: { ...SYSTEM_INPUT_NODE.data, order: 0 },
          };
          return [inputNodeWithOrder, ...processorNodes, ...subAgentNodes];
        })();

    // Import reconstructExecutionEdges to rebuild execution flow
    const { reconstructExecutionEdges } = require("../utils/executionFlow");

    // Reconstruct execution flow edges from step order (main pipeline only)
    const executionEdges = reconstructExecutionEdges(pipeline.steps);

    // Create data flow edges based on input_artifact_keys relationships
    const dataEdges: Edge[] = [];

    // Main pipeline data edges
    pipeline.steps.forEach((step) => {
      // For each input key, create a data edge
      Object.entries(step.input_artifact_keys || {}).forEach(
        ([inputKey, ref]: [string, any]) => {
          dataEdges.push({
            id: `data-${ref.from_step}-${ref.key}-to-${step.step_id}-${inputKey}`,
            source: ref.from_step,
            sourceHandle: `output-${ref.key}`,
            target: step.step_id,
            targetHandle: `input-${inputKey}`,
            type: "default",
            animated: false,
            style: {
              stroke: "#3b82f6",
              strokeWidth: 2,
            },
          });
        }
      );

      // NOTE: Sub-agent data edges are now handled recursively in loadSubAgentsRecursive()
    });

    const edges: Edge[] = [
      ...executionEdges,
      ...subAgentEdges,
      ...dataEdges,
      ...dataEdgesFromSubAgents, // Include recursively collected data edges
    ];

    // Check for pipeline loading issues
    const warnings: string[] = [];

    // Check for missing next_step in sub-agent chains
    pipeline.steps.forEach((step) => {
      if (step.config?.sub_agents) {
        const subAgents = step.config.sub_agents as any[];
        subAgents.forEach((subAgent) => {
          if (!subAgent.steps || subAgent.steps.length === 0) return;

          // Check each step except the last one for missing next_step
          let hasMissingNextStep = false;
          for (
            let stepIndex = 0;
            stepIndex < subAgent.steps.length - 1;
            stepIndex++
          ) {
            const subStep = subAgent.steps[stepIndex];
            if (!subStep.next_step) {
              hasMissingNextStep = true;
              break;
            }
          }

          if (hasMissingNextStep) {
            warnings.push(
              `Sub-agent execution flow bug detected in "${
                subAgent.display_name || subAgent.name
              }". Save pipeline to fix.`
            );
          }
        });
      }
    });

    // Check for orphaned data connections (targeting inputs that no longer exist)
    const nodeLookup = new Map(nodes.map((n) => [n.id, n]));
    const orphanedEdges = new Set<string>();

    dataEdges.forEach((edge) => {
      const targetNode = nodeLookup.get(edge.target);
      if (!targetNode) return;

      // For processors with dynamic inputs (supports_dynamic_inputs in config)
      const supportsDynamicInputs = targetNode.data.config?.supports_dynamic_inputs;
      if (!supportsDynamicInputs) return;

      // Extract the input name from the target handle (e.g., "input-task" -> "task")
      const targetHandle = edge.targetHandle;
      if (!targetHandle || !targetHandle.startsWith("input-")) return;

      const inputName = targetHandle.replace(/^input-/, "");

      // Check if this input exists in ANY of:
      // 1. custom_inputs (user-defined dynamic inputs)
      // 2. required_input_keys (standard required inputs)
      // 3. optional_input_keys (standard optional inputs)
      const customInputs = targetNode.data.custom_inputs || [];
      const requiredInputKeys = targetNode.data.required_input_keys || [];
      const optionalInputKeys = targetNode.data.optional_input_keys || [];

      const existsInCustomInputs = customInputs.some(
        (ci: any) => (typeof ci === "string" ? ci : ci.name) === inputName
      );
      const existsInRequiredInputs = requiredInputKeys.includes(inputName);
      const existsInOptionalInputs = optionalInputKeys.includes(inputName);

      const inputExists =
        existsInCustomInputs ||
        existsInRequiredInputs ||
        existsInOptionalInputs;

      if (!inputExists) {
        orphanedEdges.add(edge.id);
        warnings.push(
          `Orphaned data connection detected to "${inputName}" input on node "${targetNode.data.name}". Save pipeline to remove.`
        );
      }
    });

    // Remove orphaned edges
    const cleanedEdges = edges.filter((e) => !orphanedEdges.has(e.id));

    set({
      pipelineId: pipeline.id,
      pipelineName: pipeline.name,
      pipelineDescription: pipeline.description || "",
      pipelineVersion: pipeline.version,
      pipelineStatus: pipeline.status,
      nodes,
      edges: cleanedEdges, // Use cleaned edges with orphaned connections removed
      isDirty: warnings.length > 0, // Mark dirty if there are warnings that need fixing
      loadingWarnings: warnings,
      originalState: {
        nodes,
        edges: cleanedEdges, // Store cleaned edges in original state too
        pipelineName: pipeline.name,
        pipelineDescription: pipeline.description || "",
      },
    });
  },

  reset: () => set(initialState),

  revertChanges: () => {
    const state = get();
    if (state.originalState) {
      set({
        nodes: state.originalState.nodes,
        edges: state.originalState.edges,
        pipelineName: state.originalState.pipelineName,
        pipelineDescription: state.originalState.pipelineDescription,
        isDirty: false,
      });
    }
  },

  saveOriginalState: () => {
    const state = get();
    set({
      originalState: {
        nodes: state.nodes,
        edges: state.edges,
        pipelineName: state.pipelineName,
        pipelineDescription: state.pipelineDescription,
      },
    });
  },

  // Validation
  setValidationResults: (results) => {
    const state = get();
    const updatedNodes = applyValidationToNodes(state.nodes, results);
    set({
      validationResults: results,
      validatedAt: new Date(),
      nodes: updatedNodes,
    });
  },

  clearValidationResults: () => {
    const state = get();
    const updatedNodes = applyValidationToNodes(state.nodes, null);
    set({
      validationResults: null,
      validatedAt: null,
      nodes: updatedNodes,
    });
  },

  // UI state
  setLoading: (isLoading) => set({ isLoading }),

  setSaving: (isSaving) => set({ isSaving }),

  markDirty: () => set({ isDirty: true }),

  markClean: () => set({ isDirty: false, loadingWarnings: [] }),

  setPanelHeight: (height) => set({ panelHeight: height }),

  // Sub-agent operations (SWRM-121)
  addSubAgent: (orchestratorId, firstNodeId, subAgentConfig) => {
    const state = get();

    // Find orchestrator node
    const orchestratorNode = state.nodes.find((n) => n.id === orchestratorId);
    if (!orchestratorNode || orchestratorNode.type !== "orchestrator") {
      console.error("Orchestrator node not found:", orchestratorId);
      return;
    }

    // Get existing sub-agents
    const existingSubAgents = (orchestratorNode.data.config?.sub_agents ||
      []) as any[];

    // Create new sub-agent config (without steps for now - will be built during serialization)
    const newSubAgent = {
      name: subAgentConfig.name,
      display_name: subAgentConfig.display_name,
      description: subAgentConfig.description,
      steps: [], // Will be populated during serialization
    };

    // Update orchestrator node config
    const updatedNodes = state.nodes.map((node) => {
      if (node.id === orchestratorId) {
        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...node.data.config,
              sub_agents: [...existingSubAgents, newSubAgent],
            },
          },
        };
      }

      // Assign first node to this sub-agent
      if (node.id === firstNodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            orchestratorNodeId: orchestratorId,
            subAgentName: subAgentConfig.name,
          },
        };
      }

      return node;
    });

    // Propagate membership to connected nodes (SWRM-121)
    const {
      detectAndUpdateSubAgentMembership,
    } = require("../utils/subAgentHelpers");
    const nodesWithPropagatedMembership = detectAndUpdateSubAgentMembership(
      updatedNodes,
      state.edges
    );

    set({ nodes: nodesWithPropagatedMembership, isDirty: true });
  },

  updateSubAgentName: (
    orchestratorId,
    oldName,
    newName,
    newDisplayName,
    newDescription
  ) => {
    const state = get();

    // Find orchestrator node
    const orchestratorNode = state.nodes.find((n) => n.id === orchestratorId);
    if (!orchestratorNode || orchestratorNode.type !== "orchestrator") {
      console.error("Orchestrator node not found:", orchestratorId);
      return;
    }

    // Update sub-agent config
    const subAgents = (orchestratorNode.data.config?.sub_agents || []) as any[];
    const updatedSubAgents = subAgents.map((sa: any) => {
      if (sa.name === oldName) {
        return {
          ...sa,
          name: newName,
          display_name:
            newDisplayName !== undefined ? newDisplayName : sa.display_name,
          description:
            newDescription !== undefined ? newDescription : sa.description,
        };
      }
      return sa;
    });

    // Update all nodes in this sub-agent
    const updatedNodes = state.nodes.map((node) => {
      if (node.id === orchestratorId) {
        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...node.data.config,
              sub_agents: updatedSubAgents,
            },
          },
        };
      }

      if (
        node.data.orchestratorNodeId === orchestratorId &&
        node.data.subAgentName === oldName
      ) {
        return {
          ...node,
          data: {
            ...node.data,
            subAgentName: newName,
          },
        };
      }

      return node;
    });

    set({ nodes: updatedNodes, isDirty: true });
  },

  removeSubAgent: (orchestratorId, subAgentName) => {
    const state = get();

    // Find orchestrator node
    const orchestratorNode = state.nodes.find((n) => n.id === orchestratorId);
    if (!orchestratorNode || orchestratorNode.type !== "orchestrator") {
      console.error("Orchestrator node not found:", orchestratorId);
      return;
    }

    // Remove sub-agent from config
    const subAgents = (orchestratorNode.data.config?.sub_agents || []) as any[];
    const updatedSubAgents = subAgents.filter(
      (sa: any) => sa.name !== subAgentName
    );

    // Update nodes - remove orchestrator references from nodes in this sub-agent
    const updatedNodes = state.nodes.map((node) => {
      if (node.id === orchestratorId) {
        return {
          ...node,
          data: {
            ...node.data,
            config: {
              ...node.data.config,
              sub_agents: updatedSubAgents,
            },
          },
        };
      }

      if (
        node.data.orchestratorNodeId === orchestratorId &&
        node.data.subAgentName === subAgentName
      ) {
        return {
          ...node,
          data: {
            ...node.data,
            orchestratorNodeId: undefined,
            subAgentName: undefined,
          },
        };
      }

      return node;
    });

    // Remove edges connected to nodes in this sub-agent
    const nodesInSubAgent = state.nodes.filter(
      (n) =>
        n.data.orchestratorNodeId === orchestratorId &&
        n.data.subAgentName === subAgentName
    );
    const nodeIdsInSubAgent = new Set(nodesInSubAgent.map((n) => n.id));

    const updatedEdges = state.edges.filter((edge) => {
      // Keep edge if it doesn't involve any nodes from the removed sub-agent
      return (
        !nodeIdsInSubAgent.has(edge.source) &&
        !nodeIdsInSubAgent.has(edge.target)
      );
    });

    set({ nodes: updatedNodes, edges: updatedEdges, isDirty: true });
  },

  assignNodeToSubAgent: (nodeId, orchestratorId, subAgentName) => {
    const state = get();

    const updatedNodes = state.nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          data: {
            ...node.data,
            orchestratorNodeId: orchestratorId,
            subAgentName,
          },
        };
      }
      return node;
    });

    set({ nodes: updatedNodes, isDirty: true });
  },

  detectSubAgentMembership: () => {
    const state = get();
    const {
      detectAndUpdateSubAgentMembership,
    } = require("../utils/subAgentHelpers");

    const updatedNodes = detectAndUpdateSubAgentMembership(
      state.nodes,
      state.edges
    );
    set({ nodes: updatedNodes });
  },

  // Custom input operations
  setPendingInputConnection: (pending) => {
    set({ pendingInputConnection: pending });
  },

  addCustomInput: (nodeId, inputName, description) => {
    const { nodes, pendingInputConnection, edges } = get();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    // Add to custom_inputs array
    const customInputs = node.data.custom_inputs || [];
    const newCustomInputs = [...customInputs, { name: inputName, description }];

    // Keep input_artifact_keys as an object (it will be populated when a connection is made)
    // No need to add anything here - onConnect will populate it with full metadata

    // Update node with new custom input
    // Don't auto-insert the variable - let validation remind the user to add it
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        custom_inputs: newCustomInputs,
        // input_artifact_keys stays unchanged - will be populated on connection
        config: {
          ...node.data.config,
          custom_inputs: newCustomInputs, // Also update in config
        },
      },
    };

    set({
      nodes: nodes.map((n) => (n.id === nodeId ? updatedNode : n)),
    });

    // If there's a pending connection, create the edge now
    if (pendingInputConnection && pendingInputConnection.nodeId === nodeId) {
      const newConnection = {
        ...pendingInputConnection.connection,
        targetHandle: `input-${inputName}`, // Use new input name
      };

      // Create edge with consistent ID format for data edges
      // Format: data-{source}-{sourceKey}-to-{target}-{inputKey}
      const sourceKey =
        newConnection.sourceHandle?.replace("output-", "") || "output";
      const inputKey = inputName;
      const newEdge = {
        id: `data-${newConnection.source}-${sourceKey}-to-${newConnection.target}-${inputKey}`,
        source: newConnection.source!,
        target: newConnection.target!,
        sourceHandle: newConnection.sourceHandle,
        targetHandle: newConnection.targetHandle,
        type: "default",
      };

      set({
        edges: [...edges, newEdge],
        pendingInputConnection: null,
      });
    }

    get().markDirty();

    // Trigger auto-revalidation if pipeline was previously validated
    // This will show the "Missing Required Variables" warning for the new input
    setTimeout(async () => {
      const currentState = get();
      if (currentState.validatedAt) {
        try {
          const { buildPipelineSteps } = require("../utils/executionFlow");
          const { apiService } = require("../services/api");
          const steps = buildPipelineSteps(
            currentState.nodes,
            currentState.edges
          );
          const results = await apiService.validatePipeline({ steps });
          get().setValidationResults(results);
        } catch (error) {
          console.error(
            "Auto-revalidation after adding custom input failed:",
            error
          );
        }
      }
    }, 500);
  },

  removeCustomInput: (nodeId, inputName) => {
    console.log("[CustomInput] removeCustomInput called:", {
      nodeId,
      inputName,
    });
    const { nodes, edges } = get();
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) {
      console.log("[CustomInput] Node not found!");
      return;
    }
    console.log("[CustomInput] Node found:", {
      nodeId: node.id,
      custom_inputs: node.data.custom_inputs,
      input_artifact_keys: node.data.input_artifact_keys,
    });

    // Remove from custom_inputs
    const customInputs = node.data.custom_inputs || [];
    const newCustomInputs = customInputs.filter(
      (ci: { name: string; description?: string }) => ci.name !== inputName
    );

    // Remove from input_artifact_keys (it's an object, not an array)
    const inputArtifactKeys = node.data.input_artifact_keys || {};
    const newInputArtifactKeys = { ...inputArtifactKeys };
    delete newInputArtifactKeys[inputName];

    // Get processor name to determine prompt key
    const processorName = node.data.processor_name;
    const userPromptKey = `${processorName}_user`;

    // Get current custom prompts for this node
    const customPrompts = node.data.config?.custom_prompts || {};
    const currentUserPrompt = customPrompts[userPromptKey];

    // If there's a custom prompt, remove the variable from it
    let updatedCustomPrompts = customPrompts;
    if (currentUserPrompt) {
      const updatedPrompt = removeVariableFromPrompt(
        currentUserPrompt,
        inputName
      );
      updatedCustomPrompts = {
        ...customPrompts,
        [userPromptKey]: updatedPrompt,
      };
    }

    // Update node with removed custom input and updated prompt
    // Clear validation errors since removing the input may fix them
    const updatedNode = {
      ...node,
      data: {
        ...node.data,
        custom_inputs: newCustomInputs,
        input_artifact_keys: newInputArtifactKeys,
        validation_errors: undefined, // Clear validation errors
        validation_warnings: undefined, // Clear validation warnings
        config: {
          ...node.data.config,
          custom_inputs: newCustomInputs, // Also update in config
          custom_prompts: updatedCustomPrompts,
        },
      },
    };

    // Remove edges connected to this input
    const newEdges = edges.filter(
      (e) => !(e.target === nodeId && e.targetHandle === `input-${inputName}`)
    );

    console.log("[CustomInput] About to update state:", {
      updatedNode: {
        id: updatedNode.id,
        custom_inputs: updatedNode.data.custom_inputs,
        input_artifact_keys: updatedNode.data.input_artifact_keys,
      },
      removedEdges: edges.length - newEdges.length,
    });

    set({
      nodes: nodes.map((n) => (n.id === nodeId ? updatedNode : n)),
      edges: newEdges,
    });

    console.log("[CustomInput] State updated successfully");
    get().markDirty();
  },

  updateEdgeColorsForTheme: (theme: "light" | "dark") => {
    const state = get();
    const executionFlowColor = theme === "dark" ? "#e5e5e5" : "#64748b";

    // Update all execution flow edges to use the new theme color
    const updatedEdges = state.edges.map((edge) => {
      // Only update execution flow edges (type: 'execution')
      // Keep sub-agent edges (green) and data flow edges (blue) unchanged
      if (edge.type === "execution" && edge.style?.stroke !== "#22c55e") {
        return {
          ...edge,
          style: {
            ...edge.style,
            stroke: executionFlowColor,
          },
        };
      }
      return edge;
    });

    set({ edges: updatedEdges });
  },
}));
